import { useEffect } from "react";
import { useLocation } from "wouter";
import {
  SITE_URL,
  type ResolvedMeta,
  resolveStaticMeta,
  matchNewsSlug,
  matchWorkSlug,
  buildNewsMeta,
  buildWorkMeta,
  normalizePath,
  DEFAULT_META,
} from "@/lib/seoStatic";

const BASE = import.meta.env.BASE_URL; // always ends with "/"

function setMetaTag(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLinkTag(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function applyMeta(meta: ResolvedMeta, path: string) {
  const canonical = `${SITE_URL}${BASE.replace(/\/$/, "")}${path}`;
  const image = `${SITE_URL}${BASE}${meta.imagePath}`;

  document.title = meta.title;
  setMetaTag("name", "description", meta.description);
  setLinkTag("canonical", canonical);

  setMetaTag("property", "og:title", meta.title);
  setMetaTag("property", "og:description", meta.description);
  setMetaTag("property", "og:url", canonical);
  setMetaTag("property", "og:image", image);
  setMetaTag("property", "og:type", "website");
  setMetaTag("property", "og:site_name", "Black Swan Productions");

  setMetaTag("name", "twitter:card", "summary_large_image");
  setMetaTag("name", "twitter:title", meta.title);
  setMetaTag("name", "twitter:description", meta.description);
  setMetaTag("name", "twitter:image", image);
}

export function Seo() {
  const [location] = useLocation();

  useEffect(() => {
    const path = normalizePath(location.split(/[?#]/)[0] || "/");

    const staticMeta = resolveStaticMeta(path);
    if (staticMeta) {
      applyMeta(staticMeta, path);
      return;
    }

    // News/work detail routes: the article/project data lives in lazily
    // loaded modules (shared with the NewsDetail/WorkDetail page chunks), so
    // resolve it asynchronously instead of bundling every body into the
    // eager main chunk. Crawlers get these tags from the server shell, so
    // the brief async gap only affects the SPA-navigation tab title.
    let cancelled = false;

    const newsSlug = matchNewsSlug(path);
    const workSlug = matchWorkSlug(path);

    (async () => {
      let meta: ResolvedMeta = DEFAULT_META;
      if (newsSlug) {
        const { getNewsArticle } = await import("../data/news");
        const article = getNewsArticle(newsSlug);
        if (article) meta = buildNewsMeta(article);
      } else if (workSlug) {
        const { getWorkProject } = await import("../data/work");
        const project = getWorkProject(workSlug);
        if (project) meta = buildWorkMeta(project);
      }
      if (!cancelled) applyMeta(meta, path);
    })();

    return () => {
      cancelled = true;
    };
  }, [location]);

  return null;
}
